import { useEffect, useState } from 'react';

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  duration: number;
  delay: number;
}

export function FloatingElements() {
  const [scrollY, setScrollY] = useState(0);
  const [particles] = useState<Particle[]>(() => 
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 10 + 15,
      delay: Math.random() * 5,
    }))
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {/* Large organic blobs */}
      <div 
        className="absolute w-[600px] h-[600px] rounded-full blur-[120px] opacity-20 blob"
        style={{
          background: 'radial-gradient(circle, hsl(8 85% 55% / 0.4) 0%, transparent 70%)',
          top: '10%',
          left: '-10%',
          transform: `translateY(${scrollY * 0.15}px)`,
        }}
      />
      <div 
        className="absolute w-[500px] h-[500px] rounded-full blur-[100px] opacity-15 blob"
        style={{
          background: 'radial-gradient(circle, hsl(8 90% 60% / 0.3) 0%, transparent 70%)',
          top: '40%',
          right: '-15%',
          transform: `translateY(${scrollY * -0.1}px)`,
          animationDelay: '2s',
        }}
      />
      <div 
        className="absolute w-[400px] h-[400px] rounded-full blur-[80px] opacity-10 blob"
        style={{
          background: 'radial-gradient(circle, hsl(45 80% 55% / 0.25) 0%, transparent 70%)',
          bottom: '20%',
          left: '20%',
          transform: `translateY(${scrollY * 0.08}px)`,
          animationDelay: '4s',
        }}
      />

      {/* Floating geometric shapes */}
      <div 
        className="absolute w-32 h-32 border border-primary/10 rounded-3xl rotate-45 float"
        style={{
          top: '25%',
          right: '15%',
          transform: `translateY(${scrollY * -0.2}px) rotate(${45 + scrollY * 0.02}deg)`,
        }}
      />
      <div 
        className="absolute w-20 h-20 border border-primary/15 rounded-full float"
        style={{
          top: '60%',
          left: '10%',
          transform: `translateY(${scrollY * 0.12}px)`,
          animationDelay: '1s',
        }}
      />
      <div 
        className="absolute w-16 h-16 border border-primary/10 rotate-12 float"
        style={{
          top: '80%',
          right: '25%',
          transform: `translateY(${scrollY * -0.15}px) rotate(${12 + scrollY * 0.03}deg)`,
          animationDelay: '2s',
        }}
      />

      {/* Small floating particles */}
      {particles.map((particle) => (
        <div
          key={particle.id}
          className="absolute rounded-full bg-primary/30"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            transform: `translateY(${scrollY * (particle.id % 2 === 0 ? 0.1 : -0.1)}px)`,
            animation: `float ${particle.duration}s ease-in-out infinite`,
            animationDelay: `${particle.delay}s`,
            boxShadow: '0 0 10px hsl(8 85% 55% / 0.3)',
          }}
        />
      ))}

      {/* Gradient lines */}
      <div 
        className="absolute w-[1px] h-[30vh] opacity-10"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, hsl(8 85% 55%) 50%, transparent 100%)',
          left: '20%',
          top: '30%',
          transform: `translateY(${scrollY * 0.3}px)`,
        }}
      />
      <div 
        className="absolute w-[1px] h-[25vh] opacity-10"
        style={{
          background: 'linear-gradient(180deg, transparent 0%, hsl(8 85% 55%) 50%, transparent 100%)',
          right: '30%',
          top: '50%',
          transform: `translateY(${scrollY * -0.2}px)`,
        }}
      />

      {/* Noise texture overlay */}
      <div className="absolute inset-0 noise-overlay opacity-50" />
    </div>
  );
}